import { memo, useEffect, useMemo, useRef, useState } from "react";
import type { RefObject } from "react";
import { Droppable, Draggable } from "@hello-pangea/dnd";
import { useInfiniteQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import queryString from "qs";
import { instance } from "api/apiBase";
import { getErrorMessage } from "utils/error";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  ImageSkeleton,
  Show
} from "shared/components";
import { Avatar } from "shared/components/Avatar";
import { NoRowsFallback } from "shared/components/DataGridTable/slices/NoRowsFallback";
import { PaginatedResponse } from "shared/types";
import { Lead } from "views/leads-id/types";
import { Stage } from "views/settings-crm/types/stage";
import { LeadsFilters } from "shared/app/LeadsTableView/types";
import { buildLeadsSearchParams } from "shared/app/LeadsTableView/helpers";
import { DEFAULT_PER_PAGE } from "shared/app/LeadsTableView/constants";
import { PipelineCard } from "./PipelineCard";

const PER_PAGE = Number(DEFAULT_PER_PAGE);
const COLUMN_STALE_TIME_MS = 60_000;
// Start fetching a column's cards just before it scrolls into view.
const PREFETCH_MARGIN = "0px 400px";

interface PipelineColumnProps {
  stage: Stage;
  filters?: LeadsFilters;
  search?: string;
  assigneeIds: number[] | null;
  starred: boolean;
  enabled: boolean;
  // Board scroll container, used as the visibility root for lazy-loading.
  scrollRootRef: RefObject<HTMLElement | null>;
}

export const PipelineColumn = memo(
  ({
    stage,
    filters,
    search,
    assigneeIds,
    starred,
    enabled,
    scrollRootRef
  }: PipelineColumnProps) => {
    const columnRef = useRef<HTMLDivElement>(null);
    // Lazy-load: only the columns scrolled into view fetch their cards, so
    // opening the board fires a few requests instead of one per stage.
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
      const node = columnRef.current;
      if (!node || isVisible) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          setIsVisible(true); // stay loaded once seen
          observer.disconnect();
        },
        { root: scrollRootRef.current, rootMargin: PREFETCH_MARGIN }
      );
      observer.observe(node);
      return () => observer.disconnect();
    }, [isVisible, scrollRootRef]);

    const propertyIds = useMemo(
      () => filters?.properties?.map((property) => property.id),
      [filters?.properties]
    );
    const sourceIds = useMemo(
      () => filters?.leadSources?.map((source) => source.id),
      [filters?.leadSources]
    );

    const {
      data,
      isLoading,
      isError,
      error,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage
    } = useInfiniteQuery({
      queryKey: [
        "LEADS_PIPELINE_COLUMN",
        stage.id,
        search,
        assigneeIds,
        propertyIds,
        sourceIds,
        filters?.activityTypes,
        filters?.leadTypes,
        filters?.createdWithin,
        filters?.createdAfter,
        filters?.createdBefore,
        starred
      ],
      queryFn: ({ pageParam }) =>
        instance
          .get(
            "/portal/v1/leads?" +
              queryString.stringify(
                buildLeadsSearchParams({
                  page: pageParam,
                  perPage: PER_PAGE,
                  stageIds: [String(stage.id)],
                  propertyIds,
                  sourceIds,
                  assigneeIds,
                  filters,
                  search,
                  starred,
                  orderBy: "most_recent_activity",
                  orderDirection: "desc"
                }),
                { arrayFormat: "brackets" }
              )
          )
          .then((res) => res.data as PaginatedResponse<Lead>),
      initialPageParam: 1,
      getNextPageParam: (lastPage) =>
        lastPage.has_more ? lastPage.current_page + 1 : undefined,
      enabled: enabled && isVisible,
      // Don't refetch every column when the view remounts (e.g. toggling
      // list<->pipeline) — reuse the cached page. Stage moves and star toggles
      // invalidate this key explicitly, so local changes still reflect right
      // away; staleTime bounds how long a backgrounded column stays cached.
      refetchOnMount: false,
      staleTime: COLUMN_STALE_TIME_MS
    });

    const leads = useMemo(
      () => data?.pages.flatMap((page) => page.items) ?? [],
      [data?.pages]
    );
    const totalCount = data?.pages[0]?.total_count ?? 0;

    // Surface a fetch failure — the in-column fallback intentionally reuses the
    // empty-state copy, so without this a degraded backend would look like an
    // empty stage. Shared toastId so a backend outage that fails every column
    // shows a single toast, not one per column.
    useEffect(() => {
      if (isError)
        toast.error(getErrorMessage(error, "Failed to load leads"), {
          toastId: "LEADS_PIPELINE_COLUMN_ERROR"
        });
    }, [isError, error]);

    return (
      <div
        ref={columnRef}
        className="flex w-80 shrink-0 flex-col space-y-2 rounded-lg bg-background-800 p-3"
      >
        <div className="flex items-center gap-2 px-1">
          <Avatar
            fallbackText=""
            fallbackBackgroundColor={stage.color}
            size={10}
          />
          <p className="truncate text-sm font-medium text-content-1000">
            {stage.name}
          </p>
          <span className="ml-auto rounded-md bg-alpha-80 px-1 text-xxs font-bold text-content-1000 shadow-xs">
            {totalCount}
          </span>
        </div>

        <Show if={isLoading}>
          <div className="space-y-2">
            {[0, 1].map((placeholder) => (
              <ImageSkeleton
                key={placeholder}
                className="h-[120px] w-full rounded-lg"
              />
            ))}
          </div>
        </Show>

        <Droppable droppableId={String(stage.id)}>
          {(provided) => (
            // flex-1 makes the droppable fill the column so the whole column is a
            // drop target — fast drags register a drop anywhere in the column.
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="min-h-[120px] flex-1 space-y-2 rounded-lg"
            >
              {/* Draggables must be direct children with the placeholder right
                after them, otherwise @hello-pangea/dnd can't register drops on
                a column that already has cards. */}
              {leads.map((lead, index) => (
                <Draggable
                  key={lead.id}
                  draggableId={String(lead.id)}
                  index={index}
                >
                  {(dragProvided, dragSnapshot) => (
                    <div
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      {...dragProvided.dragHandleProps}
                    >
                      <PipelineCard
                        lead={lead}
                        isDragging={dragSnapshot.isDragging}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
              <Show if={isError}>
                <NoRowsFallback text="There are no cards to display" />
              </Show>
              <Show if={hasNextPage}>
                <Button
                  variant={ButtonVariant.BASE}
                  size={ButtonSize.SM}
                  loading={isFetchingNextPage}
                  onClick={() => fetchNextPage()}
                >
                  Load more
                </Button>
              </Show>
            </div>
          )}
        </Droppable>
      </div>
    );
  }
);

PipelineColumn.displayName = "PipelineColumn";
